import React, {useState, useEffect, useMemo} from 'react'
import Searchbar from 'app/components/Searchbar'
import EmptyState from 'app/components/EmptyState'
import ReactPaginate from 'react-paginate'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import checkToControlMapping from '../../services/CheckToControlMapping.json'
import {RegionsDisplay} from '../RegionsDisplay'
import Button from 'app/components/Button'
import ExcludeCategoryModal from '../modal/ExcludeCategoryModal'
import {ToastContainer} from 'react-toastify'

interface CloudData {
  id: string
  type: string
  purpose: string
  name: string
  account_id: string
  severity: string
  open_issues: string
  ignored: string
  last_scanned: any
  created_at: any
  scan_results?: any[]
}

interface CloudChecksProps {
  cloud: CloudData
  onCloudUpdate?: (updated: CloudData) => void
}

interface ScanResult {
  category: string
  description: string
  message: string
  plugin: string
  resource: string
  status: string
  title: string
  controls?: string[]
  regions?: string[]
}

const CloudChecks: React.FC<CloudChecksProps> = ({cloud, onCloudUpdate}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<ScanResult[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [scanResults, setScanResults] = useState<ScanResult[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [selectedNumber, setSelectedNumber] = useState('50')
  const [openExcludeModal, setOpenExcludeModal] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true)

    if (cloud?.scan_results) {
      const results: ScanResult[] = []
      const scanResultsData = cloud.scan_results as any
      const excluded = (cloud as any)?.excluded_categories || []

      if (Array.isArray(scanResultsData)) {
        const mappedResults = scanResultsData.map((result: any) => {
          const controls = checkToControlMapping.mapping[result.plugin] || []
          return {
            ...result,
            controls,
          }
        })
        const visibleResults = mappedResults.filter((r) => !excluded.includes(r.category))
        setScanResults(visibleResults)
      } else {
        Object.keys(scanResultsData).forEach((key) => {
          if (key.startsWith('item_')) {
            const item = scanResultsData[key]
            if (item && typeof item === 'object') {
              let result: ScanResult
              if (item.item_0) {
                result = item.item_0 as ScanResult
              } else {
                result = item as ScanResult
              }
              const controls = checkToControlMapping.mapping[result.plugin] || []
              results.push({
                ...result,
                controls,
              })
            }
          }
        })
        const visibleResults = results.filter((r) => !excluded.includes(r.category))
        setScanResults(visibleResults)
      }
    } else {
      setScanResults([])
    }

    setTimeout(() => {
      setIsLoading(false)
    }, 500)
  }, [cloud])

  useEffect(() => {
    const excluded = (cloud as any)?.excluded_categories || []
    if (excluded.includes(selectedCategory)) {
      setSelectedCategory('All')
    }
  }, [cloud, selectedCategory])

  const availableCategories = useMemo(() => {
    const categories = Array.from(new Set(scanResults.map((result) => result.category)))
    return categories.sort()
  }, [scanResults])

  useEffect(() => {
    setCurrentPage(0)
  }, [selectedCategory, searchTerm])

  useEffect(() => {
    let filtered = scanResults

    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter((result) => result.category === selectedCategory)
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (result) =>
          result.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.plugin.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredItems(filtered)
  }, [scanResults, selectedCategory, searchTerm])

  // Pagination logic
  const itemsPerPage = parseInt(selectedNumber)
  const pageCount = Math.ceil(filteredItems.length / itemsPerPage)
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = filteredItems.slice(startIndex, endIndex)

  const handlePageClick = (event: {selected: number}) => {
    setCurrentPage(event.selected)
  }

  if (!cloud) {
    return (
      <div className='card card-custom'>
        <div className='card-body py-3'>
          <EmptyState
            title='Cloud data not available'
            description='Unable to load cloud information. Please try refreshing the page.'
            isLoading={false}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='card card-custom'>
        <div className='card-header border-0 pt-6 h-100'>
          <div className='card-title'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          <div className='card-toolbar'>
            <div className='d-flex align-items-center justify-content-end gap-3'>
              <div className='d-flex align-items-center gap-3'>
                <select
                  id='categoryFilter'
                  className='form-select'
                  aria-label='Select Category'
                  value={selectedCategory}
                  style={{width: '150px', height: '40px', fontSize: '12px'}}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={isLoading}
                >
                  <option value='All'>All Categories</option>
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <Button
                  btnType='default'
                  className='gap-2'
                  onClick={() => setOpenExcludeModal(true)}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-eye-off'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M10.585 10.587a2 2 0 0 0 2.829 2.828' />
                    <path d='M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87' />
                    <path d='M3 3l18 18' />
                  </svg>
                  Exclude Category
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body py-3'>
          <div
            className='table-responsive'
            style={isLoading ? {height: '500px', overflowY: 'auto'} : {}}
          >
            <table className='table align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold fs-7 border-bottom border-dotted' style={{color: '#5A5A5A'}}>
                  <th className='min-w-200px ps-4'>TITLE</th>
                  <th className='min-w-250px'>DESCRIPTION</th>
                  <th className='min-w-100px'>MAPPED CONTROLS</th>
                  <th className='min-w-100px'>CATEGORY</th>
                  <th className='min-w-150px'>REGIONS</th>
                  <th className='min-w-100px'>COMPLIANCE</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <>
                    <tr>
                      <td colSpan={7} style={{height: '100px'}}></td>
                    </tr>
                    <tr>
                      <td colSpan={7} className='text-center'>
                        <div className='d-flex flex-column align-items-center justify-content-center'>
                          <div className='spinner-border text-primary mb-3' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={7} style={{height: '200px'}}></td>
                    </tr>
                  </>
                ) : currentItems.length > 0 ? (
                  currentItems.map((result, index) => (
                    <tr key={`${result.plugin}-${index}`} className='border-bottom border-dotted'>
                      {/* <td>
                      <div className='d-flex align-items-center mx-5'>
                        <span className='text-gray-900 fs-7'>{result.plugin}</span>
                      </div>
                    </td> */}
                      <td>
                        <div className='d-flex align-items-center ps-4'>
                          <span className='fw-semibold text-dark'>{result.title}</span>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <span className='text-gray-900 fs-7'>{result.description}</span>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          {result.controls && result.controls.length > 0 ? (
                            result.controls.length === 1 ? (
                              <span className='badge badge-light-primary fs-7 fw-bold'>
                                {result.controls[0]}
                              </span>
                            ) : (
                              <div className='d-flex align-items-center gap-1'>
                                <span className='badge badge-light-primary fs-7 fw-bold'>
                                  {result.controls[0]}
                                </span>
                                <OverlayTrigger
                                  placement='right'
                                  popperConfig={{
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, 8],
                                        },
                                      },
                                    ],
                                  }}
                                  overlay={
                                    <Tooltip
                                      id={`tooltip-controls-${index}`}
                                      style={{whiteSpace: 'pre-line', textAlign: 'left'}}
                                    >
                                      <span
                                        style={{
                                          fontSize: '12px',
                                          display: 'block',
                                          textAlign: 'left',
                                        }}
                                        className='fw-semibold'
                                      >
                                        {result.controls
                                          .slice(1)
                                          .map((control, idx) => control)
                                          .join('\n')}
                                      </span>
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className='badge px-3 rounded-pill'
                                    style={{border: '1px solid #E1E1E1'}}
                                  >
                                    +{result.controls.length - 1}
                                  </span>
                                </OverlayTrigger>
                              </div>
                            )
                          ) : (
                            <span className='text-muted fs-7'>--</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          {result.category ? (
                            <span className='badge badge-light-info fs-7 fw-bold'>
                              {result.category}
                            </span>
                          ) : (
                            <span className='text-muted fs-7'>--</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <RegionsDisplay
                            mappings={result.regions?.join(', ') || ''}
                            showAll={false}
                            toggleShow={() => {}}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <span
                            className={`badge ${
                              result.status === 'OK' || result.status === 'PASS'
                                ? 'badge-light-success'
                                : 'badge-light-danger'
                            } fs-7 fw-bold`}
                          >
                            {result.status === 'OK' || result.status === 'PASS'
                              ? 'Compliant'
                              : 'Non-compliant'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState
                        title={
                          scanResults.length === 0 ? 'No scan results available' : 'No checks found'
                        }
                        description={
                          scanResults.length === 0
                            ? "This cloud hasn't been scanned yet. Run a scan to see security checks and compliance results."
                            : 'No security checks found matching your search criteria.'
                        }
                        isLoading={false}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredItems.length >= 15 && (
            <div className='d-flex justify-content-between'>
              <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start dt-toolbar'>
                <div>
                  <select
                    name='kt_cloud_checks_table_length'
                    aria-controls='kt_cloud_checks_table'
                    className='form-select'
                    id='dt-length-1'
                    value={selectedNumber}
                    onChange={(e) => setSelectedNumber(e.target.value)}
                  >
                    <option value='50'>50</option>
                    <option value='100'>100</option>
                    <option value='150'>150</option>
                  </select>
                </div>
              </div>

              <div className='d-flex justify-content-end my-5'>
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  breakLabel={'...'}
                  breakClassName={'break-me'}
                  pageCount={pageCount}
                  marginPagesDisplayed={1}
                  pageRangeDisplayed={1}
                  onPageChange={handlePageClick}
                  containerClassName={'pagination'}
                  activeClassName={'active'}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ExcludeCategoryModal
        isOpen={openExcludeModal}
        onClose={() => setOpenExcludeModal(false)}
        categories={availableCategories}
        cloud={cloud}
        onUpdated={(excluded) => {
          if (onCloudUpdate) {
            onCloudUpdate({...cloud, excluded_categories: excluded} as any)
          }
        }}
      />
      <ToastContainer />
    </>
  )
}

export default CloudChecks
